import { asStableCallback } from "@bezoned/shared/hooks/use-stable-callback.js";
import type { TeamMember } from "@bezoned/shared/types/api/team/team-member-list.types.js";
import {
  Button,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Tooltip,
} from "@fluentui/react-components";
import { DeleteRegular } from "@fluentui/react-icons";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import type {
  CellContext,
  ColumnDef,
  Table as ReactTable,
} from "@tanstack/react-table";
import { createContext, useContext, useMemo } from "react";

export type TeamMemberRow = TeamMember;

interface TeamMembersTableContextValue {
  currentUserId: string | null;
}

const TeamMembersTableContext =
  createContext<TeamMembersTableContextValue | null>(null);

export const useTeamMembersTableContext = () => {
  const context = useContext(TeamMembersTableContext);

  if (!context) {
    throw new Error(
      "useTeamMembersTableContext must be used within TeamMembersTable"
    );
  }

  return context;
};

type TeamMemberActionCellProps = CellContext<TeamMember, unknown> & {
  isPending: boolean;
  onRemoveUser: (user: TeamMember) => void;
};

const TeamMemberActionCell = ({
  row,
  isPending,
  onRemoveUser,
}: TeamMemberActionCellProps) => {
  const { currentUserId } = useTeamMembersTableContext();
  const isSelf = Boolean(currentUserId) && row.original.id === currentUserId;
  const isDisabled = isPending || isSelf;
  const button = (
    <Button
      appearance="subtle"
      aria-label={`Remove ${row.original.displayName}`}
      disabled={isDisabled}
      icon={<DeleteRegular />}
      onClick={asStableCallback(() => onRemoveUser(row.original))}
      type="button"
    />
  );

  if (!isSelf) {
    return <div className="flex justify-end">{button}</div>;
  }

  return (
    <div className="flex justify-end">
      <Tooltip content="You cannot delete yourself" relationship="description">
        {button}
      </Tooltip>
    </div>
  );
};

interface UseTeamMembersTableParams {
  data: TeamMember[];
  searchTerm: string;
  pageIndex: number;
  pageSize: number;
  isRemovePending: boolean;
  onRemoveUser: (user: TeamMember) => void;
}

export const useTeamMembersTable = ({
  data,
  searchTerm,
  pageIndex,
  pageSize,
  isRemovePending,
  onRemoveUser,
}: UseTeamMembersTableParams) => {
  const columns = useMemo<ColumnDef<TeamMember>[]>(
    () => [
      {
        accessorKey: "displayName",
        cell: (info) => info.getValue(),
        header: "Name",
      },
      {
        accessorKey: "email",
        cell: (info) => info.getValue(),
        header: "Email",
      },
      {
        cell: (info) => (
          <TeamMemberActionCell
            {...info}
            isPending={isRemovePending}
            onRemoveUser={onRemoveUser}
          />
        ),
        header: () => <span className="block w-full text-right">Action</span>,
        id: "actions",
      },
    ],
    [isRemovePending, onRemoveUser]
  );

  return useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    globalFilterFn: "includesString",
    state: {
      globalFilter: searchTerm,
      pagination: {
        pageIndex,
        pageSize,
      },
    },
  });
};

interface TeamMembersTableProps {
  table: ReactTable<TeamMember>;
  isLoading: boolean;
  currentUserId: string | null;
  errorMessage: string | null;
}

export const TeamMembersTable = ({
  table,
  isLoading,
  currentUserId,
  errorMessage,
}: TeamMembersTableProps) => {
  const columnCount = table.getVisibleLeafColumns().length;
  const contextValue = useMemo(() => ({ currentUserId }), [currentUserId]);
  const emptyMessage = errorMessage ?? "No office members found.";
  const emptyMessageClassName = errorMessage
    ? "text-red-600 text-sm"
    : "text-bzd-gray-600 text-sm";

  return (
    <TeamMembersTableContext.Provider value={contextValue}>
      <Table aria-label="Office members">
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHeaderCell key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </TableHeaderCell>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {Boolean(isLoading) && (
            <TableRow>
              <TableCell colSpan={columnCount}>
                <div className="text-bzd-gray-600 flex items-center gap-2 text-sm">
                  <Spinner size="tiny" />
                  Loading office members...
                </div>
              </TableCell>
            </TableRow>
          )}
          {!isLoading && table.getRowModel().rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={columnCount}>
                <div className={emptyMessageClassName}>{emptyMessage}</div>
              </TableCell>
            </TableRow>
          )}
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TeamMembersTableContext.Provider>
  );
};
